import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchNodeGraph as fetchNodeGraphService } from "services/modules/ecs/data";
import { setThreatDetails } from "stores/modules/ecs/datareducer";
import { fetchThreatActorsById as fetchThreatActorService} from 'services/modules/vapt/data';

import * as am4core from "@amcharts/amcharts4/core";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import * as am4plugins_forceDirected from "@amcharts/amcharts4/plugins/forceDirected";
import * as am4charts from "@amcharts/amcharts4/charts";

import InfoIcon from "@mui/icons-material/Info";
import BugReportIcon from '@mui/icons-material/BugReport';
import { CircularProgress } from "@mui/material";
import PropTypes from 'prop-types';

am4core.useTheme(am4themes_animated);

const ThreatActorGeography = ({ height }) => {
  // React router
  const navigate = useNavigate();

  // Redux dispatch
  const dispatch = useDispatch();
  
  // useRef hook
  const chartDivRef = useRef("chartdiv");
  
  // Redux selectors
  const threatactors = useSelector((state) => state.ecsData.threatDetails);
  const selectedthreatactors = useSelector((state) => state.ecsData.selectedThreatDetails);
  const vaptThreatActorsContainer = useSelector(state => state.vaptData.threatActors);
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData );
  const authenticatedUserContainer = useSelector((state) => state.auth.user);
  
  // State Variables
  const [threatData, setThreatData] = useState([]);
  const [selectedActors, setSelectedActors] = useState([]);
  const [loading, setLoading] = useState(true); 
  const [threatActors, setThreatActors] = useState([]);
  
  // Fetches threat actors data based on company id
  const fetchVAPTThreatActorsData = async() => {
    let params = {
      "id":selectedCompanyContainer.companyid,
    }
    dispatch(fetchThreatActorService({ params }));
  };

  useEffect(() => {
    if (selectedCompanyContainer) {
      if (selectedCompanyContainer?.companyid === authenticatedUserContainer?.company?.id) {
        authenticatedUserContainer?.company?.applications?.forEach(element => {
           if(element.slug === "vapt"){
            fetchVAPTThreatActorsData();
          }
        });
      } else {
        if (authenticatedUserContainer?.company?.customers?.length > 0) {
          const companyCustomers = authenticatedUserContainer.company.customers;
          const customersWithSameCompanyId = companyCustomers.filter(customer => customer.id === selectedCompanyContainer.companyid);
          if (customersWithSameCompanyId.length > 0) {
            customersWithSameCompanyId[0].applications.forEach(element => {
              if(element.slug === "vapt"){
                fetchVAPTThreatActorsData();
              }
            });
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyContainer]);
  
  // combines both ecs and vapt threat actors
  useEffect(() => {
    let combinedThreatActors = [];
    if(threatactors && vaptThreatActorsContainer){
      
      const uniqueActorNames = vaptThreatActorsContainer
      .map(actor => actor.name) // Get all actor names
      .filter((name, index, self) => self.indexOf(name) === index);

      combinedThreatActors = [
        ...threatactors,
        ...uniqueActorNames
      ];
    }
    else if(threatactors){
      combinedThreatActors = [...threatactors];
    }
    else if(vaptThreatActorsContainer){
      combinedThreatActors = vaptThreatActorsContainer
      .map(actor => actor.name) 
      .filter((name, index, self) => self.indexOf(name) === index);
    }
    if(combinedThreatActors){
      setThreatActors(combinedThreatActors);
    }
  }, [threatactors, vaptThreatActorsContainer]);

  // Fetch data only when threatactors or selectedCompany changes
  useEffect(() => {
    const fetchNodeGraphData = async () => {
      setLoading(true);  // Trigger loading state
      const threatActorsString = threatActors?.join(",");
      let params = { threatactors: threatActorsString };
      const response = await fetchNodeGraphService(params);
      setThreatData(response?.data?.transformedData || []);
      setLoading(false); 
    };

    if (threatActors?.length > 0) {
      fetchNodeGraphData();
    } else {
      setThreatData([]); 
      setLoading(false);
    }
  }, [threatActors]); 

  // Initialize chart and update it when threatData changes
  useEffect(() => {
    if (threatData.length > 0) {
      let chart = am4core.create(chartDivRef.current, am4plugins_forceDirected.ForceDirectedTree);
      let networkSeries = chart.series.push(new am4plugins_forceDirected.ForceDirectedSeries());

      // Configure chart
      chart.legend = new am4charts.Legend();
      chart.legend.position = "right";
      chart.legend.labels.template.fontSize = 14;
      chart.legend.labels.template.fill = am4core.color("#151515");
      chart.legend.labels.template.fontWeight = "bold";

      // Legend and Node Styling
      networkSeries.colors.list = [
        am4core.color("#f44336"),
        am4core.color("#007FFF"),
        am4core.color("#008000"),
        am4core.color("#03befc"),
      ];
      networkSeries.nodes.template.togglable = false;
      networkSeries.dataFields.linkWith = "linkWith";
      networkSeries.dataFields.name = "name";
      networkSeries.dataFields.id = "name";
      networkSeries.dataFields.value = "value";
      networkSeries.dataFields.children = "children";
      networkSeries.nodes.template.label.text = "{name}";

      // Node interactions 
      networkSeries.nodes.template.events.on("hit", function (event) {
        let dataItem = event.target.dataItem;
        if (threatactors.includes(dataItem.name))
          navigate(`/tip/threat?threat=${dataItem.name}`);
      });

      // Set data
      networkSeries.data = threatData;

      return () => {
        chart.dispose();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threatData, threatactors]);  

  // Handle actor selection for dispatching threat details
  const toggleActorSelection = (actor) => {
    let updatedActors = selectedActors.includes(actor)
      ? selectedActors.filter((selectedActor) => selectedActor !== actor)
      : [...selectedActors, actor];

    setSelectedActors(updatedActors);

    if (updatedActors.length === 0) {
      dispatch(setThreatDetails(selectedthreatactors));
    } else {
      dispatch(setThreatDetails(updatedActors));
    }
  };

  return (
    <div>
      <p className="carousel-title p-4">
        Threat Actors
      </p>
      {loading ? (
        <div className="loader-c dived-card align-center">
          <CircularProgress size={70} />
        </div>
      ) : threatData.length > 0 ? (
        <div>
          <div ref={chartDivRef} id="chartdiv" style={{ width: "100%", height: `${height}px` }}></div>
          <div className="col p-4 dived-card">
            {selectedthreatactors?.map((actor, index) => (
              <span 
                key={index} 
                onClick={() => toggleActorSelection(actor)}  
                style={{ cursor: 'pointer', fontSize: '0.9rem', padding: '10px', color: selectedActors.includes(actor) ? 'red' : 'rgb(21, 21, 21)' }}>
                <BugReportIcon className="fs-6 text-danger me-1 mb-1" />
                <span className="fw-bold">{actor}</span>
              </span>
            ))}
          </div>
        </div>
      ) : (
        <div className="loader-c dived-card align-center">
          <InfoIcon className="crq-info-icon" /> <br />
          <strong className="fs-5">No Threat Actors Found</strong>
        </div>
      )}
    </div>
  );
};

ThreatActorGeography.defaultProps = {
  height: 500,
};

ThreatActorGeography.propTypes = {
  height: PropTypes.number,
};

export default ThreatActorGeography;
